import React, { useState, useEffect } from 'react';
import { LiveKitRoom, LocalUserChoices, RoomAudioRenderer } from '@livekit/components-react';
import { LayoutContextProvider } from '@livekit/components-react';
import { VideoConferenceCustom } from '@/app/components/VideoConferenceCustom';
import { Room } from 'livekit-client';
import { ConnectionDetails } from '@/lib/types';
import { useCharacterData, useActiveImage, useGlobalLiveKitRoomState, useChatStatus } from '../../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../../data/liveMessageData/liveMessage';
import { ChatStatus } from '../../../data/globalChatState';
import AudioUnlocker from '@/app/components/AudioUnlocker';
import { AdvancedImage } from '@cloudinary/react';
import { getPresignedUrl } from '@/app/lib/actions/s3';

export function VideoStream() {

  // Get data from global state
  const { character: avatar } = useCharacterData();
  const { loadingStatus: activeImageStatus, imageData: activeImageData, imageUri } = useActiveImage();
  const { chatStatus, setChatStatus } = useChatStatus();
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  
  const avatarId = avatar?.metadata.avatar_id || '';
  const [presignedUrl, setPresignedUrl] = useState<string>('');
  // Handle async presigned URL generation
  useEffect(() => {
    const fetchPresignedUrl = async () => {
      try {
        const { presignedUrl: url } = await getPresignedUrl(imageUri || '');
        setPresignedUrl(url);
      } catch (error) {
        console.error('Failed to get presigned URL:', error);
        setPresignedUrl('');
      }
    };

    if (imageUri) {
      fetchPresignedUrl();
    }
  }, [imageUri]);
  const cloudinaryImage = activeImageStatus === LiveImageStatus.LOADED ? activeImageData : undefined;

  // Create a render key that changes for each new connection
  const renderKey = React.useMemo(() => {
    const cd = globalLKRoomState.connectionDetails;
    if (!cd) return 'lk-empty';
    // token changes every time you hit /connection-details (iat/exp differ)
    return `${cd.roomName}:${cd.participantToken.slice(-12)}`;
  }, [globalLKRoomState.connectionDetails]);

  return (
    <>
      {/* Global styles for LiveKit components */}
      <style jsx global>{`
        /* Override LiveKit CSS custom properties to remove borders and outlines */
        [data-lk-theme="default"] {
          --lk-accent-bg: transparent;
          --lk-accent-fg: transparent;
          --lk-border-radius: 0px;
        }
  
        .lk-video-conference {
          height: 100% !important;
          width: 100% !important;
        }
        .lk-video-conference-inner {
          height: 100% !important;
          width: 100% !important;
          margin: 0 !important;
        }
        .lk-grid-layout-wrapper {
          height: 100% !important;
          width: 100% !important;
        }
        .lk-grid-layout {
          height: 100% !important;
          width: 100% !important;
        }
        .lk-participant-tile {
          height: 100% !important;
          width: 100% !important;
        }
        .lk-participant-tile video {
          height: 100% !important;
          width: 100% !important;
          object-fit: cover !important;
          object-position: top !important;
          /* iOS Safari video rendering optimizations */
          -webkit-transform: translateZ(0) !important;
          transform: translateZ(0) !important;
          -webkit-backface-visibility: hidden !important;
          backface-visibility: hidden !important;
          -webkit-perspective: 1000 !important;
          perspective: 1000 !important;
          -webkit-transform-style: preserve-3d !important;
          transform-style: preserve-3d !important;
          /* Prevent iOS Safari video scaling issues */
          -webkit-image-rendering: -webkit-optimize-contrast !important;
          image-rendering: -webkit-optimize-contrast !important;
          /* Force hardware acceleration */
          will-change: transform !important;
          /* Prevent iOS Safari from applying its own video optimizations */
          -webkit-video-playable-inline: true !important;
        }
        /* iOS Safari specific container optimizations - removed transforms to fix positioning */
        .lk-control-bar {
          display: none !important;
        }
        .lk-participant-metadata {
          display: none !important;
        }
        .lk-connection-quality {
          display: none !important;
        }
        .lk-participant-name {
          display: none !important;
        }
        .lk-speaking-indicator {
          display: none !important;
        }
        .lk-participant-tile.lk-speaking {
          border: none !important;
          box-shadow: none !important;
        }
        .lk-focus-toggle-button {
          display: none !important;
        }
        
        /* iOS Safari specific optimizations */
        @supports (-webkit-touch-callout: none) {
          .lk-participant-tile video {
            object-fit: cover !important;
            object-position: top !important;
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            -webkit-backface-visibility: hidden !important;
            backface-visibility: hidden !important;
            -webkit-perspective: 1000 !important;
            perspective: 1000 !important;
            -webkit-transform-style: preserve-3d !important;
            transform-style: preserve-3d !important;
            -webkit-overflow-scrolling: touch !important;
            overflow-scrolling: touch !important;
            /* Prevent iOS Safari video controls */
            -webkit-media-controls: none !important;
            -webkit-media-controls-panel: none !important;
            -webkit-media-controls-play-button: none !important;
            -webkit-media-controls-start-playback-button: none !important;
          }
          
          .lk-video-conference {
            -webkit-overflow-scrolling: touch !important;
            overflow-scrolling: touch !important;
          }
        }
      `}</style>

      <div className="relative w-full h-full drop-shadow-lg">
        
        {/* Show original image until video is detected */}
        {(chatStatus === ChatStatus.INFO || chatStatus === ChatStatus.LOADING) && cloudinaryImage && (
          <div className="relative w-full h-full">
            <div
              className="absolute inset-0 w-full h-full rounded-l-[5px] z-10 transition-all duration-500 ease-in-out overflow-hidden"
              style={{
                filter: (globalLKRoomState.isInitiatingRoom || chatStatus === ChatStatus.LOADING) ? 'blur(4px) brightness(0.4)' : 'none'
              }}
            >
              <AdvancedImage
                cldImg={cloudinaryImage}
                alt="Character image"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        )}
        
        {/* LiveKit Video - hidden until video is detected */}
        {globalLKRoomState.room && globalLKRoomState.preJoinChoices && globalLKRoomState.connectionDetails && (
          <div className="relative w-full h-full">
            <div
              data-lk-theme="default"
              style={{
                height: '100%',
                width: '100%',
                borderRadius: '5px 0 0 5px',
                overflow: 'hidden',
                position: chatStatus === ChatStatus.LIVE ? 'relative' : 'absolute',
                top: chatStatus === ChatStatus.LIVE ? 'auto' : 0,
                left: chatStatus === ChatStatus.LIVE ? 'auto' : 0,
                opacity: chatStatus === ChatStatus.LIVE ? 1 : 0,
                zIndex: chatStatus === ChatStatus.LIVE ? 20 : 1,
                transition: 'opacity 0.5s ease-in-out',
                backgroundColor: '#000'
              }}
            >
            <LiveKitRoom // wrap in Livekit room because we use messaging. When code reaches here, room must be connected
              key={renderKey} // Force full re-mount of LK UI tree
              room={globalLKRoomState.room}
              token={globalLKRoomState.connectionDetails.participantToken}
              serverUrl={globalLKRoomState.connectionDetails.serverUrl}
              video={globalLKRoomState.preJoinChoices.videoEnabled}
              audio={globalLKRoomState.preJoinChoices.audioEnabled}
              onDisconnected={() => {
                // Return to regular chat page when disconnected
                setChatStatus(ChatStatus.INFO);
              }}
              style={{ height: '100%', width: '100%' }}
            >
              <AudioUnlocker />
              <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                {chatStatus === ChatStatus.LIVE && (
                  <LayoutContextProvider key={`layout-${renderKey}`}>
                    <VideoConferenceCustom 
                      key={`vc-${renderKey}`} // Also remount your grid-level component
                      hideControlBar={true}
                      alwaysHideChat={true}
                      prompt={avatar?.metadata.prompt || undefined}
                      scene={avatar?.metadata.scene_prompt || undefined}
                      bio={avatar?.metadata.agent_bio || undefined}
                      avatar_name={avatar?.metadata.avatar_name || 'Unknown Avatar'}
                      presignedUrl={presignedUrl}
                      audioChannels="gpu" // Use default LiveKit behavior temporarily
                      style={{ height: '100%', width: '100%' }}
                    />
                  </LayoutContextProvider>
                )}
              </div>
            </LiveKitRoom>
            </div>
          </div>
        )}
      </div>
    </>
  );
} 